import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X, GripHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Radix locks `body { pointer-events: none }` for the duration of a modal and
 * releases it on close. That release is reference-counted across every mounted
 * dialog, and on pages that mount several of them (the customer page has the
 * inspection, address, contact, archive and confirm dialogs at once) the count
 * can be left standing — the lock survives the close and the entire app stops
 * responding to clicks until the user reloads. Reproduced on production: body
 * went `auto` -> `none` on open -> still `none` after close with zero dialogs
 * open. Release it ourselves once nothing is open any more.
 */
function releaseBodyPointerLock(): void {
  if (typeof document === 'undefined') return;
  if (!document.querySelector('[role="dialog"][data-state="open"]')) {
    document.body.style.pointerEvents = '';
  }
}

export const Dialog = ({
  open,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) => {
  // Watch the `open` prop rather than onOpenChange: Radix only fires that
  // callback for closes it initiates (Esc, the ✕, an outside click). Most of
  // this app closes its dialogs by setting state directly — `handleSave` calls
  // setOpen(false) — which never reaches onOpenChange, and that is exactly the
  // path that was stranding the lock. The prop changes either way.
  React.useEffect(() => {
    if (open !== false) return;
    const t = setTimeout(releaseBodyPointerLock, 350);
    return () => clearTimeout(t);
  }, [open]);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} {...props} />
  );
};
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-[60] bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, style, ...props }, ref) => {
  // Draggable modal (client 24 July "see what is behind the modal"). Grab the
  // top handle to reposition; the inline transform overrides the centering
  // classes only once moved, so the open animation is untouched.
  const [pos, setPos] = React.useState({ x: 0, y: 0 });
  const drag = React.useRef<{
    sx: number;
    sy: number;
    ox: number;
    oy: number;
  } | null>(null);
  const onDown = (e: React.PointerEvent) => {
    drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    setPos({
      x: drag.current.ox + e.clientX - drag.current.sx,
      y: drag.current.oy + e.clientY - drag.current.sy,
    });
  };
  const onUp = (e: React.PointerEvent) => {
    drag.current = null;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
  };
  const moved = pos.x !== 0 || pos.y !== 0;
  // Navigating away unmounts the content without an onOpenChange, so release
  // the lock here too or the next page loads unclickable.
  React.useEffect(
    () => () => {
      setTimeout(releaseBodyPointerLock, 0);
    },
    []
  );
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        style={
          moved
            ? {
                transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px))`,
                ...style,
              }
            : style
        }
        className={cn(
          'fixed left-[50%] top-[50%] z-[60] grid w-full max-w-lg max-h-[90dvh] overflow-y-auto translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-4 sm:p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
          className
        )}
        {...props}
      >
        <div
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          className="absolute left-1/2 top-1 -translate-x-1/2 cursor-move touch-none rounded p-1 text-muted-foreground/40 hover:text-muted-foreground"
          aria-hidden
        >
          <GripHorizontal className="h-4 w-4" />
        </div>
        {children}
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

export const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className
    )}
    {...props}
  />
);
export const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
  />
);
export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
